import { SimulationConfig } from '../types';

export class PerformanceTelemetry {
  public static fps = 0;
  
  // Latency Metrics (ms)
  public static cpuTime = 0;
  public static gpuTime = 0;             // CPU side submission/command preparation time
  public static gpuHardwareTime = 0;     // Actual GPU-side hardware execution time from WebGPU timestamp query
  public static gpuQuerySupported = false;
  
  // Worker-specific Profiling (ms)
  public static workerRTT = 0;
  public static workerActive = false;
  public static workerMode: 'SAB' | 'TRANSFERABLE' | 'NONE' = 'NONE';
  private static workerLastSentTime = 0;

  // Off-Thread Recorder / Video Compiler Telemetry
  public static recorderActive = false;
  public static recorderCompositingTime = 0;
  public static recorderBacklog = 0;
  public static recorderFramesCompleted = 0;
  
  // Graphics draw operations
  public static webglDrawCalls = 0;
  public static webgpuDrawCalls = 0;
  public static canvas2DDraws = 0;

  // Trackers
  public static vertexCountUploaded = 0;
  public static indexCountDrawn = 0;
  public static bytesUploaded = 0;
  public static isWebGPUActive = false;
  public static webgpuTier = 0; // 0: WebGL fallback, 1: Low-end WebGPU, 2: Mid WebGPU, 3: High-end WebGPU
  
  // VRAM Estimation
  public static approximatedVRAM = 0; // in MB
  public static vramDetails = {
    vertexBuffers: 0,      // in bytes
    indexBuffers: 0,       // in bytes
    uniformBuffers: 0,     // in bytes
    storageBuffers: 0,     // in bytes
    texturesAndFB: 0,      // in bytes
    queryBuffers: 0        // in bytes
  };
  
  // Moving averages (to prevent visual jitter)
  private static cpuBuffer = new Float32Array(30);
  private static gpuBuffer = new Float32Array(30);
  private static gpuHardwareBuffer = new Float32Array(30);
  private static rttBuffer = new Float32Array(15);
  private static cpuRingIdx = 0;
  private static gpuRingIdx = 0;
  private static gpuHardwareRingIdx = 0;
  private static rttRingIdx = 0;

  /**
   * Tracks CPU computation time for physics, logic and updates
   */
  public static recordCpuTime(durationMs: number) {
    this.cpuBuffer[this.cpuRingIdx] = durationMs;
    this.cpuRingIdx = (this.cpuRingIdx + 1) % this.cpuBuffer.length;
    
    let sum = 0;
    for (let i = 0; i < this.cpuBuffer.length; i++) {
      sum += this.cpuBuffer[i];
    }
    this.cpuTime = sum / this.cpuBuffer.length;
  }

  /**
   * Tracks GPU/drawing command preparation & execution time (CPU Dispatch)
   */
  public static recordGpuTime(durationMs: number) {
    this.gpuBuffer[this.gpuRingIdx] = durationMs;
    this.gpuRingIdx = (this.gpuRingIdx + 1) % this.gpuBuffer.length;
    
    let sum = 0;
    for (let i = 0; i < this.gpuBuffer.length; i++) {
      sum += this.gpuBuffer[i];
    }
    this.gpuTime = sum / this.gpuBuffer.length;
  }

  /**
   * Tracks actual GPU hardware execution duration from timestamp query
   */
  public static recordGpuHardwareTime(durationMs: number) {
    if (isNaN(durationMs) || durationMs < 0 || durationMs > 1000) return;
    this.gpuHardwareBuffer[this.gpuHardwareRingIdx] = durationMs;
    this.gpuHardwareRingIdx = (this.gpuHardwareRingIdx + 1) % this.gpuHardwareBuffer.length;
    
    let sum = 0;
    let count = 0;
    for (let i = 0; i < this.gpuHardwareBuffer.length; i++) {
      if (this.gpuHardwareBuffer[i] > 0) {
        sum += this.gpuHardwareBuffer[i];
        count++;
      }
    }
    this.gpuHardwareTime = count > 0 ? (sum / count) : durationMs;
  }

  /**
   * Triggers when physics step is submitted to the worker thread
   */
  public static recordWorkerSent() {
    this.workerLastSentTime = performance.now();
    this.workerActive = true;
  }

  /**
   * Triggers when updated data returns from the worker thread
   */
  public static recordWorkerReceived() {
    if (this.workerLastSentTime > 0) {
      const rtt = performance.now() - this.workerLastSentTime;
      this.rttBuffer[this.rttRingIdx] = rtt;
      this.rttRingIdx = (this.rttRingIdx + 1) % this.rttBuffer.length;
      
      let sum = 0;
      for (let i = 0; i < this.rttBuffer.length; i++) {
        sum += this.rttBuffer[i];
      }
      this.workerRTT = sum / this.rttBuffer.length;
    }
  }

  /**
   * Clears accumulated frame counters before a new render loop passes
   */
  public static resetFrameCounters() {
    this.webglDrawCalls = 0;
    this.webgpuDrawCalls = 0;
    this.canvas2DDraws = 0;
    this.vertexCountUploaded = 0;
    this.indexCountDrawn = 0;
    this.bytesUploaded = 0;
  }

  /**
   * Accumulates WebGL, WebGPU, or Canvas draw calls
   */
  public static incrementDrawCalls(api: 'webgl' | 'webgpu' | 'canvas', count: number = 1) {
    if (api === 'webgl') {
      this.webglDrawCalls += count;
    } else if (api === 'webgpu') {
      this.webgpuDrawCalls += count;
    } else {
      this.canvas2DDraws += count;
    }
  }

  /**
   * Accumulates uploaded vertices and index allocations
   */
  public static recordVertexUpload(verticesUploaded: number, bytes: number) {
    this.vertexCountUploaded += verticesUploaded;
    this.bytesUploaded += bytes;
  }

  public static recordIndexCount(indicesDrawn: number) {
    this.indexCountDrawn += indicesDrawn;
  }

  /**
   * Accurately calculates approximate GPU Memory utilization based on active buffers
   */
  public static updateVRAMEstimation(config: SimulationConfig, ballCount: number, width: number, height: number) {
    const isGraphicsActive = config.gpuPingPongTrailsEnabled !== false || config.gpuParticlesEnabled !== false || this.isWebGPUActive;
    if (!isGraphicsActive) {
      this.approximatedVRAM = 0.0;
      this.vramDetails = { vertexBuffers: 0, indexBuffers: 0, uniformBuffers: 0, storageBuffers: 0, texturesAndFB: 0, queryBuffers: 0 };
      return;
    }

    const resWidth = width || window.innerWidth || 1920;
    const resHeight = height || window.innerHeight || 1080;

    let vBuffer = 0;
    let iBuffer = 0;
    let uBuffer = 0;
    let sBuffer = 0;
    let texFB = 0;
    let qBuffer = 0;

    if (this.isWebGPUActive) {
      // 1. Static vertex buffers
      vBuffer += 4 * 8 * 4; // Quad vertices = 128 bytes
      
      // 2. Instance buffers capacity (allocated dynamically)
      const maxBalls = config.maxBalls || 2000;
      vBuffer += maxBalls * 8 * 4; // instanceData size estimate
      vBuffer += 1024 * 8 * 4;    // rings bounds buffer (approx.)
      vBuffer += 512 * 8 * 4;     // blast buffer (approx.)
      vBuffer += 4096 * 8 * 4;    // trail buffer (approx.)

      // 3. Uniform buffers
      uBuffer += 32; // camera
      uBuffer += 48; // ringGlobal
      uBuffer += 32; // blastGlobal
      uBuffer += 32; // trailGlobal

      // 4. Textures / Framebuffers
      // Browser canvas swapchain format bgra8unorm texture estimation
      texFB += resWidth * resHeight * 4;

      // 5. Query buffers
      if (this.gpuQuerySupported) {
        qBuffer += 16; // Query resolve buffer
        qBuffer += 16; // Query readback buffer
      }
    } else {
      // WebGL Mode
      vBuffer += 4 * 12; // 48 bytes static quad geometry
      const maxBallsLimit = config.maxBalls || 2000;
      vBuffer += maxBallsLimit * 12 * 4; // Instancing floats

      if (config.gpuPingPongTrailsEnabled !== false) {
        // WebGL: 2 framebuffers for ping-pong trails
        texFB += 2 * (resWidth * resHeight * 4);
      }

      if (config.gpuParticlesEnabled !== false) {
        const maxParticles = 32000;
        vBuffer += 2 * maxParticles * 7 * 4; // double buffered particles
      }
    }

    this.vramDetails = {
      vertexBuffers: vBuffer,
      indexBuffers: iBuffer,
      uniformBuffers: uBuffer,
      storageBuffers: sBuffer,
      texturesAndFB: texFB,
      queryBuffers: qBuffer
    };

    const totalBytes = vBuffer + iBuffer + uBuffer + sBuffer + texFB + qBuffer;
    this.approximatedVRAM = Number((totalBytes / (1024 * 1024)).toFixed(2)); // Return size in MB
  }
}

